import React, { useMemo } from "react";
import { useI18n } from "../hooks/i18n-hooks";
import { ProfileType, TimeSheetState } from "../__generated__/graphql";
import { TimeSheetFieldsFragment } from "../__generated__/operations/graphql";
import { useNavigation } from "@react-navigation/native";
import { RootStackParamList } from "../navigations/RootNavigation";
import { StackScreenProps } from "@react-navigation/stack";
import { getWeekNumber, minutesAsString } from "../utils";
import { useTheme } from "../hooks/use-theme";
import { LiftTheme } from "../theme";
import ListGroup from "./ListGroup";
import { IN_LIST_FORMAT, useDateFormatter, useIntervalFormatter } from "../hooks/date";
import IconComposition from "./IconComposition";
import { LabelMode } from "../ui_atoms/Label";

const ICON_STATUE_MAP: Record<TimeSheetState, (theme: LiftTheme) => React.ReactNode> = {
    accepted({ colors }): React.ReactNode {
        return (
            <IconComposition
                backgroundColor={colors.successBackground}
                iconColor={colors.onSuccessBackground}
                icon="check"
                size={17}
            />
        );
    },
    open({ colors }): React.ReactNode {
        return (
            <IconComposition
                backgroundColor={colors.warningBackground}
                iconColor={colors.onWarningBackground}
                icon="edit"
                size={13}
            />
        );
    },
    rejected({ colors }): React.ReactNode {
        return (
            <IconComposition
                backgroundColor={colors.errorBackground}
                iconColor={colors.onErrorBackground}
                icon="close"
                size={17}
            />
        );
    },
    submitted({ colors }): React.ReactNode {
        return (
            <IconComposition
                backgroundColor={colors.infoBackground}
                iconColor={colors.onInfoBackground}
                icon="send"
                size={15}
            />
        );
    },
};

type ScreenParams = StackScreenProps<RootStackParamList>;

type TimesheetListProps = {
    profileType: ProfileType;
    timeSheets: TimeSheetFieldsFragment[];
    hideMissionName?: boolean; // do not display client nor worker name
};

const TimesheetList: React.FC<TimesheetListProps> = ({
    profileType,
    timeSheets,
    hideMissionName = false,
}) => {
    const i18n = useI18n();
    const theme = useTheme();

    const navigation = useNavigation<ScreenParams["navigation"]>();
    const isWorker = useMemo(() => profileType === ProfileType.Worker, [profileType]);

    const currentYear = useMemo(() => new Date().getFullYear(), []);
    const intervalFormatter = useIntervalFormatter();
    const dateFormatter = useDateFormatter();
    const timeSheetItems = useMemo(
        () =>
            timeSheets.map((ts) => {
                const [year, week] = getWeekNumber(new Date(ts.fromDate));
                const missionName = ts.missionInfo?.name;
                const userName = isWorker
                    ? ts.missionInfo?.clientInfo?.name
                    : ts.missionInfo?.workerInfo?.name;

                const duration = intervalFormatter(new Date(ts.fromDate), new Date(ts.toDate));
                const hours = minutesAsString(ts.totalTrackedDurationMinutes);
                const tags = [];
                if (ts.status === TimeSheetState.Rejected && ts.rejectedAt) {
                    const when = dateFormatter(new Date(ts.rejectedAt), IN_LIST_FORMAT);
                    tags.push(i18n.t("time_sheet.rejected_info", { when }));
                } else if (ts.status === TimeSheetState.Accepted && ts.acceptedAt) {
                    const when = dateFormatter(new Date(ts.acceptedAt), IN_LIST_FORMAT);
                    tags.push(i18n.t("time_sheet.accepted_info", { when }));
                } else if (ts.status === TimeSheetState.Submitted && ts.submittedAt) {
                    const when = dateFormatter(new Date(ts.submittedAt), IN_LIST_FORMAT);
                    tags.push(i18n.t("time_sheet.submitted_info", { when }));
                }

                if (ts.status === TimeSheetState.Submitted)
                    if (ts.totalTrackedDurationMinutes > 0) {
                        tags.push(
                            i18n.t("time_sheet.hours_completed", {
                                hours,
                                count: Math.floor(ts.totalTrackedDurationMinutes),
                            }),
                        );
                    } else {
                        tags.push(i18n.t("time_sheet.declared_inactive"));
                    }
                let needCompletionLabel: string | undefined = undefined;
                if (profileType === ProfileType.Worker && ts.status === TimeSheetState.Open) {
                    needCompletionLabel = i18n.t("time_sheet.complete_timesheet_request");
                }
                if (profileType === ProfileType.Worker && ts.status === TimeSheetState.Rejected) {
                    needCompletionLabel = i18n.t("time_sheet.change_timesheet_request");
                }

                return {
                    title:
                        currentYear == year
                            ? i18n.t("time_sheet.title_same_year", { week })
                            : i18n.t("time_sheet.title_other_year", { week, year }),
                    description: hideMissionName
                        ? duration
                        : `${duration}\n${userName || ""}${missionName ? `\n${missionName}` : ""}`,
                    iconComponent: ICON_STATUE_MAP[ts.status](theme),
                    value: ts.id,
                    isItemsCheckable: false,
                    tags,
                    labels: [
                        {
                            text: needCompletionLabel,
                            mode: needCompletionLabel ? ("cta" as LabelMode) : undefined,
                        },
                    ],
                    onPress: () => {
                        if (profileType === ProfileType.Client) {
                            navigation.navigate("TimesheetClientReview", { id: ts.id });
                        }
                        if (profileType === ProfileType.Worker) {
                            if (
                                ts.status === TimeSheetState.Open ||
                                ts.status === TimeSheetState.Rejected
                            ) {
                                navigation.navigate("TimesheetWorkerInput", { id: ts.id });
                            } else {
                                navigation.navigate("TimesheetWorkerReview", { id: ts.id });
                            }
                        }
                    },
                };
            }),
        [
            timeSheets,
            isWorker,
            intervalFormatter,
            profileType,
            currentYear,
            i18n,
            hideMissionName,
            theme,
            dateFormatter,
            navigation,
        ],
    );

    return <ListGroup items={timeSheetItems} />;
};

export default TimesheetList;
